import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { DesktopSidePanel } from '@/components/common/admin/desktop/panel/DesktopSidePanel';
import { DesktopPanelHeader } from '@/components/common/admin/desktop/panel/DesktopPanelHeader';
import { DesktopPanelBody } from '@/components/common/admin/desktop/panel/DesktopPanelBody';
import { WebConfirmModal } from '@/components/common/modals/desktop/DesktopConfirmModal';
import { ActionButtons } from '@/components';
import { CORK_BOARD_INPUT_LIMITS } from '@/constants/residents/corkBoardConstants';
import { GlassFormInput } from '@/components/admin/redesign';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { TagColorPicker } from '../TagColorPicker';
import { TagPill } from '../TagPill';
import { TagIconPicker } from '../TagIconPicker';
import type { CorkBoardTag } from '@/types/residents/corkBoard';

interface DesktopTagPanelProps {
  visible: boolean;
  tag: CorkBoardTag | null;
  isSaving: boolean;
  onSave: (data: { name: string; color: string | null; icon: string | null }) => void;
  onClose: () => void;
}

export const DesktopTagPanel: React.FC<DesktopTagPanelProps> = ({
  visible,
  tag,
  isSaving,
  onSave,
  onClose,
}) => {
  const { redesign } = useAdminColors();
  const isEditing = !!tag;
  const [name, setName] = useState('');
  const [color, setColor] = useState<string | null>(null);
  const [icon, setIcon] = useState<string | null>(null);
  const [nameError, setNameError] = useState('');
  const [showDiscardModal, setShowDiscardModal] = useState(false);

  useEffect(() => {
    if (visible) {
      setName(tag?.name ?? '');
      setColor(tag?.color ?? null);
      setIcon(tag?.icon ?? null);
      setNameError('');
      setShowDiscardModal(false);
    }
  }, [visible, tag]);

  const validate = useCallback((): boolean => {
    const trimmed = name.trim();
    if (!trimmed) {
      setNameError('Tag name is required');
      return false;
    }
    if (trimmed.length > CORK_BOARD_INPUT_LIMITS.tagNameMax) {
      setNameError(`Name must be ${CORK_BOARD_INPUT_LIMITS.tagNameMax} characters or fewer`);
      return false;
    }
    setNameError('');
    return true;
  }, [name]);

  const handleSave = useCallback(() => {
    if (isSaving || !validate()) return;
    onSave({
      name: name.trim(),
      color: color ?? null,
      icon: icon ?? null,
    });
  }, [isSaving, validate, onSave, name, color, icon]);

  const handleCancel = useCallback(() => {
    const isDirty =
      name !== (tag?.name ?? '') ||
      color !== (tag?.color ?? null) ||
      icon !== (tag?.icon ?? null);
    if (isDirty) {
      setShowDiscardModal(true);
    } else {
      onClose();
    }
  }, [name, color, icon, tag, onClose]);

  return (
    <DesktopSidePanel visible={visible} onClose={handleCancel} width={480}>
      <WebConfirmModal
        visible={showDiscardModal}
        title="Discard Changes"
        message="Are you sure you want to discard your changes?"
        confirmText="Discard"
        cancelText="Keep Editing"
        onConfirm={() => {
          setShowDiscardModal(false);
          onClose();
        }}
        onCancel={() => setShowDiscardModal(false)}
      />
      <View style={styles.container}>
        <DesktopPanelHeader
          title={isEditing ? 'Edit Tag' : 'New Tag'}
          onClose={handleCancel}
        />
        <DesktopPanelBody scroll bottomInset={75}>
          <View style={styles.bodyContent}>
            <GlassFormInput
              label="Name"
              required
              value={name}
              onChangeText={(text) => {
                setName(text);
                if (nameError) setNameError('');
              }}
              placeholder="e.g. Events, For Sale, Help Needed"
              maxLength={CORK_BOARD_INPUT_LIMITS.tagNameMax}
              error={nameError || undefined}
              autoFocus
            />

            <TagColorPicker selectedColor={color} onSelectColor={setColor} />

            <TagIconPicker selectedIcon={icon} onSelectIcon={setIcon} />

            {(name.trim() || color || icon) && (
              <View style={styles.previewSection}>
                <Text style={[styles.label, { color: redesign.text.secondary }]}>Preview</Text>
                <View style={styles.previewRow}>
                  <TagPill name={name.trim() || 'Tag Name'} color={color} icon={icon} />
                </View>
              </View>
            )}
          </View>
        </DesktopPanelBody>
        <View style={[styles.footer, { borderTopColor: redesign.card.border }]}>
          <ActionButtons
            isLoading={isSaving}
            onCancel={handleCancel}
            onSubmit={handleSave}
            submitText={isEditing ? 'Update' : 'Create'}
            containerStyle={{ backgroundColor: redesign.card.bg }}
          />
        </View>
      </View>
    </DesktopSidePanel>
  );
};


const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  footer: {
    borderTopWidth: 1,
  },
  bodyContent: {
    paddingHorizontal: 18,
    paddingTop: 8,
    gap: 20,
  },
  label: {
    fontSize: 13,
    fontWeight: '500',
  },
  previewSection: {
    gap: 8,
    paddingTop: 4,
  },
  previewRow: {
    flexDirection: 'row',
  },
});
